<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
        <h2 class="text-base font-bold m-0">
          {{ editMenu ? 'Edit Menu' : 'Add New Menu' }}
        </h2>
        <button @click="closeModal" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="p-5 flex-1 flex flex-col gap-3">
        <!-- Form fields -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Department -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.75rem] font-semibold text-[#1e293b]">Department</label>
            <select
              v-model="form.departmentId"
              class="px-3 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white"
              required
            >
              <option value="">Select department</option>
              <option v-for="dep in departments" :key="dep.id" :value="dep.id">
                {{ dep.name }}
              </option>
            </select>
          </div>

          <!-- Sub Department -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.75rem] font-semibold text-[#1e293b]">Sub Department (optional)</label>
            <select
              v-model="form.subDepartmentId"
              class="px-3 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white"
              :disabled="!form.departmentId"
            >
              <option disabled value="">Select sub department</option>
              <option v-for="sub in subDepartments" :key="sub.id" :value="sub.id">
                {{ sub.name }}
              </option>
            </select>
          </div>

          <!-- Name -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.75rem] font-semibold text-[#1e293b]">Name</label>
            <input
              v-model.trim="form.name"
              type="text"
              placeholder="Enter menu name"
              class="px-3 py-1 border rounded-md outline-none transition-all text-sm placeholder:text-slate-400"
              :class="errorMessage ? 'border-red-400 focus:border-red-500' : 'border-[#cbd5e1] focus:border-[#2563eb]'"
              required
            />
            <p v-if="errorMessage" class="text-red-500 text-xs mt-0.5 flex items-center gap-1">
              <svg class="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd" />
              </svg>
              {{ errorMessage }}
            </p>
          </div>

          <!-- Total Price -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.75rem] font-semibold text-[#1e293b]">Total Price</label>
            <input
              v-model.number="form.totalPrice"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              class="px-3 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm placeholder:text-slate-400"
              required
            />
          </div>

          <!-- Status Flags -->
          <div class="flex flex-wrap items-center gap-x-6 gap-y-2 md:col-span-2 mt-1">
            <!-- Is taxable -->
            <div class="flex items-center gap-2">
              <input disabled
                id="isTaxable"
                v-model="form.isTaxable"
                type="checkbox"
                class="h-4 w-4 text-[#0d4169] border-[#cbd5e1] rounded focus:ring-[#0d4169]"
              />
              <label for="isTaxable" class="text-[0.8rem] font-semibold text-[#1e293b]">
                Taxable
              </label>
            </div>

            <!-- Is for staff -->
            <div class="flex items-center gap-2">
              <input
                id="isForStaff"
                v-model="form.isForStaff"
                type="checkbox"
                class="h-4 w-4 text-[#0d4169] border-[#cbd5e1] rounded focus:ring-[#0d4169]"
              />
              <label for="isForStaff" class="text-[0.8rem] font-semibold text-[#1e293b]">
                Is for staff
              </label>
            </div>

            <!-- Status -->
            <!-- <div class="flex items-center gap-2">
              <input
                id="status"
                v-model="form.status"
                type="checkbox"
                class="h-4 w-4 text-[#0d4169] border-[#cbd5e1] rounded focus:ring-[#0d4169]"
              />
              <label for="status" class="text-[0.8rem] font-semibold text-[#1e293b]">
                Status
              </label>
            </div> -->

            <!-- Is available -->
            <!-- <div class="flex items-center gap-2">
              <input
                id="isAvailable"
                v-model="form.isAvailable"
                type="checkbox"
                class="h-4 w-4 text-[#0d4169] border-[#cbd5e1] rounded focus:ring-[#0d4169]"
              />
              <label for="isAvailable" class="text-[0.8rem] font-semibold text-[#1e293b]">
                Available
              </label>
            </div> -->
          </div>
        </div>

        <div class="mt-2 p-2 bg-slate-50 rounded-md text-[11px] text-slate-600 flex flex-col gap-1 border border-slate-100">
          <div class="flex justify-between">
            <span class="font-bold">Original Price</span>
            <span class="font-bold">{{ computedAmounts.price.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="font-semibold">Addition ({{ additionPercentLabel }}%)</span>
            <span>{{ computedAmounts.addition.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="font-semibold">VAT ({{ vatPercentLabel }}%)</span>
            <span>{{ computedAmounts.vat.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between border-t border-slate-200 pt-1 mt-1">
            <span class="font-bold text-[#0d4169]">Total Selling Price</span>
            <span class="font-bold text-[#0d4169]">{{ computedAmounts.totalPrice.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between border-t border-slate-200 pt-1 mt-1">
            <span class="font-bold text-emerald-700">Profit In Birr</span>
            <span class="font-bold text-emerald-700">{{ profitInBirr.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between border-t border-slate-200 pt-1 mt-1">
            <span class="font-bold text-emerald-700">Cost Percentage</span>
            <span class="font-bold text-emerald-700">{{ costPercentage.toFixed(2) }}%</span>
          </div>
        </div>

        <!-- Packaging & Image Row -->
        <div class="grid grid-cols-2 gap-4 mt-2 pt-2 border-t border-slate-100">
          <!-- Packaging Fee -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.75rem] font-semibold text-[#1e293b]">Packaging Fee</label>
            <input
              v-model.number="form.packagingFee"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              class="px-3 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm placeholder:text-slate-400"
            />
          </div>

          <!-- Image Upload Box -->
          <div class="flex flex-col gap-1">
            <label class="text-[0.75rem] font-semibold text-[#1e293b]">Menu Image (Optional)</label>
            <label 
              for="menu-image-upload" 
              class="flex items-center justify-center border border-dashed border-slate-200 rounded-md p-1 cursor-pointer hover:border-[#2563eb] hover:bg-slate-50 transition-all group h-[33px] relative overflow-hidden"
            >
              <div v-if="form.image" class="w-full h-full flex items-center justify-center relative">
                <img :src="resolveCompanyLogoUrl(form.image)" class="max-w-full max-h-7 object-contain">
                <button @click.prevent="form.image = ''" class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow-sm hover:bg-red-600 z-10 font-bold">✕</button>
              </div>
              <div v-else class="flex items-center gap-2 text-slate-300 group-hover:text-[#2563eb]">
                 <svg v-if="!uploadingImage" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                 <svg v-else class="w-4 h-4 animate-spin text-[#2563eb]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                 <span class="text-[11px] font-semibold text-slate-400 group-hover:text-[#2563eb]">{{ uploadingImage ? 'Uploading...' : 'Upload Image' }}</span>
              </div>
              <input id="menu-image-upload" type="file" class="hidden" accept="image/*" @change="handleImageUpload">
            </label>
          </div>
        </div>

        <div class="mt-2 pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
          <button
            type="button"
            @click="closeModal"
            class="px-5 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="min-w-[100px] px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex justify-center items-center gap-2"
          >
            <div
              v-if="saving"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!saving">{{ editMenu ? 'Update' : 'Save Menu' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineExpose, defineProps, onMounted, reactive, ref, watch } from 'vue'
import store from '../../store'
import { apiFetch, normalizeUploadValue, resolveCompanyLogoUrl } from '../../utils/api'

const props = defineProps({
  isOpen: Boolean,
  editMenu: { type: Object, default: null },
})

const emit = defineEmits(['close', 'save', 'update'])

const departments = ref([])
const subDepartments = ref([])
const isHydratingEdit = ref(false)
const saving = ref(false)
const uploadingImage = ref(false)
const errorMessage = ref('')

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const userId = computed(() => currentUser.value?.id || null)
const companyConstants = ref({
  tax: 15,
  addition: 10,
})

const getInitialForm = () => ({
  id: null,
  departmentId: '',
  subDepartmentId: '',
  name: '',
  totalPrice: 0,
  isForStaff: false,
  isAvailable: true,
  status: true,
  isTaxable: true,
  packagingFee: 0,
  image: '',
})

const form = reactive(getInitialForm())

// Holds cost from the menu being edited; 0 for new registration
const editCost = ref(0)
const costPercentage = computed(() => {
  const cost = Number(editCost.value) || 0
  const price = computedAmounts.value.price
  return ((cost / price) * 100)
})

const profitInBirr = computed(() => {
  const cost = Number(editCost.value) || 0
  const price = computedAmounts.value.price
  return price - cost
})

const computedAmounts = computed(() => {
  const total = Number(form.totalPrice) || 0
  const additionRate = Math.max(0, Number(companyConstants.value.addition) || 0) / 100
  const vatRate = Math.max(0, Number(companyConstants.value.tax) || 0) / 100
  
  if (!form.isTaxable) {
    // Formula: Total = Base * (1 + additionRate)
    const divisor = 1 + additionRate
    const price = +(total / (divisor || 1)).toFixed(2)
    const addition = +(price * additionRate).toFixed(2)
    return { totalPrice: total, vat: 0, addition, price }
  }

  // Formula: Total = Base * (1 + additionRate) * (1 + vatRate)
  const divisor = (1 + additionRate) * (1 + vatRate)
  const price = +(total / (divisor || 1)).toFixed(2)
  const addition = +(price * additionRate).toFixed(2)
  const vat = +((price + addition) * vatRate).toFixed(2)
  
  return { totalPrice: total, vat, addition, price }
})

const additionPercentLabel = computed(() => {
  const n = Math.max(0, Number(companyConstants.value.addition) || 0)
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
})

const vatPercentLabel = computed(() => {
  const n = Math.max(0, Number(companyConstants.value.tax) || 0)
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
})

async function loadCompanyConstants() {
  if (!companyId.value) return
  try {
    const res = await apiFetch('/settings/company-constants')
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    companyConstants.value = {
      tax: Number(data?.tax ?? 15),
      addition: Number(data?.addition ?? 10),
    }
  } catch (err) {
    console.error('Error loading company constants for menu modal:', err)
  }
}

async function handleImageUpload(event) {
  const file = event.target.files[0]
  if (!file) return

  // 3MB limit
  if (file.size > 3 * 1024 * 1024) {
    alert('File is too large. Max 3MB.')
    event.target.value = ''
    return
  }

  uploadingImage.value = true
  try {
    const formData = new FormData()
    formData.append('logo', file) // Using 'logo' field name as expected by your /upload endpoint

    const res = await apiFetch('/upload', {
      method: 'POST',
      body: formData
    })

    if (res.ok) {
      const data = await res.json()
      form.image = normalizeUploadValue(data.url)
    } else {
      throw new Error('Upload failed')
    }
  } catch (err) {
    console.error('Error uploading menu image:', err)
    alert('Failed to upload menu image.')
  } finally {
    uploadingImage.value = false
    event.target.value = ''
  }
}

async function loadDepartments() {
  try {
    if (!companyId.value && !branchId.value) return
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)
    const response = await apiFetch(`/departments?${params.toString()}`, { method: 'GET' })
    if (!response.ok) return
    const data = await response.json().catch(() => [])
    departments.value = Array.isArray(data) ? data : []
    departments.value = departments.value.filter(d => d.status === 1);
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error loading departments for menu list:', err)
  }
}

async function loadSubDepartments(departmentId) {
  try {
    subDepartments.value = []
    if (!departmentId) return
    const response = await apiFetch(`/sub-departments/department/${departmentId}`, { method: 'GET' })
    if (!response.ok) return
    const data = await response.json().catch(() => [])
    subDepartments.value = Array.isArray(data) ? data : []
    subDepartments.value = subDepartments.value.filter(s => s.status === 1);
    subDepartments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error loading sub-departments for menu list:', err)
  }
}

watch(
  () => props.editMenu,
  (val) => {
    if (val) {
      isHydratingEdit.value = true
      editCost.value = Number(val.cost ?? val.Cost ?? 0) || 0
      Object.assign(form, {
        id: val.id,
        departmentId: val.departmentId || val.department_id || val.department?.id || '',
        subDepartmentId: val.subDepartmentId || val.sub_department_id || val.subDepartment?.id || '',
        name: val.name || '',
        totalPrice: val.totalPrice ?? val.total_price ?? 0,
        isForStaff: (val.isForStaff ?? val.is_for_staff ?? 0) === 1,
        isAvailable: (val.isAvailable ?? val.is_available ?? 1) === 1,
        status: (val.status ?? 1) === 1,
        isTaxable: (val.isTaxable ?? val.is_taxable ?? 1) === 1,
        packagingFee: val.packagingFee ?? val.packaging_fee ?? 0,
        image: normalizeUploadValue(val.image),
      })
      if (form.departmentId) loadSubDepartments(form.departmentId)
      setTimeout(() => { isHydratingEdit.value = false }, 0)
    } else {
      editCost.value = 0
      Object.assign(form, getInitialForm())
      isHydratingEdit.value = false
    }
    errorMessage.value = ''
  },
  { immediate: true }
)

watch(
  () => form.departmentId,
  (newVal) => {
    if (isHydratingEdit.value) {
      if (newVal) loadSubDepartments(newVal)
      else subDepartments.value = []
      return
    }

    // user changed department → reset sub department selection
    form.subDepartmentId = ''
    if (newVal) loadSubDepartments(newVal)
    else subDepartments.value = []
  }
)

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      loadDepartments()
      loadCompanyConstants()
      if (!props.editMenu) {
        Object.assign(form, getInitialForm())
      }
    }
    errorMessage.value = ''
    saving.value = false
  }
)

onMounted(() => {
  if (props.isOpen) {
    loadDepartments()
    loadCompanyConstants()
  }
})

function closeModal() {
  Object.assign(form, getInitialForm())
  errorMessage.value = ''
  saving.value = false
  emit('close')
}

async function handleSubmit() {
  errorMessage.value = ''
  saving.value = true

  const trimmedName = String(form.name || '').trim()
  const parsedDepartmentId = Number(form.departmentId)
  const parsedTotal = Number(computedAmounts.value.totalPrice || 0)

  if (!trimmedName) {
    errorMessage.value = 'Menu name is required.'
    saving.value = false
    return
  }
  if (!Number.isFinite(parsedDepartmentId)) {
    errorMessage.value = 'Department is required.'
    saving.value = false
    return
  }
  if (!(parsedTotal > 0)) {
    errorMessage.value = 'Total Price must be greater than 0.'
    saving.value = false
    return
  }

  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)

    const res = await apiFetch(`/menu-lists?${params.toString()}`, { method: 'GET' })
    if (res.ok) {
      const list = await res.json().catch(() => [])
      const exists = (Array.isArray(list) ? list : []).some((m) => {
        const depId = Number(m?.departmentId ?? m?.department_id ?? m?.department?.id ?? NaN)
        const sameDepartment = depId === parsedDepartmentId
        const sameName = String(m?.name || '').trim().toLowerCase() === trimmedName.toLowerCase()
        const isSelf = props.editMenu && Number(m?.id) === Number(form.id)
        return sameDepartment && sameName && !isSelf
      })
      if (exists) {
        errorMessage.value = 'Menu name already exists in this department.'
        saving.value = false
        return
      }
    }
  } catch {
    // Let backend validation be the source of truth if pre-check fails.
  }

  const payload = {
    id: form.id,
    departmentId: form.departmentId,
    subDepartmentId: form.subDepartmentId || null,
    name: form.name,
    totalPrice: computedAmounts.value.totalPrice,
    price: computedAmounts.value.price,
    addition: computedAmounts.value.addition,
    vat: computedAmounts.value.vat,
    isForStaff: form.isForStaff,
    isAvailable: form.isAvailable,
    status: form.status,
    isTaxable: form.isTaxable,
    packagingFee: form.packagingFee,
    image: normalizeUploadValue(form.image),
    companyId: branchId.value ? null : companyId.value,
    branchId: branchId.value || null,
    userId: userId.value,
  }

  if (props.editMenu) {
    emit('update', payload)
  } else {
    emit('save', payload)
  }
}

// Called by parent to surface API errors inline
function setError(msg) {
  errorMessage.value = msg
  saving.value = false
}

defineExpose({ setError })
</script>

<style scoped>
.animate-in {
  animation: 0.2s ease-out forwards;
}
.fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.zoom-in {
  from { transform: scale(0.97); }
  to { transform: scale(1); }
}
</style>
